import React from "react";
import { fetchQuery, graphql, RelayEnvironmentProvider, useFragment } from "react-relay";

import { faLockAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, groupBy, sortBy } from "lodash";
import { el, setAttr } from "redom";

import { Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import { getTypeIcon } from "~/common/StreamSelect";
import ViewportContext, { Viewport } from "~/common/ViewportContext";
import * as dom from "~/lib/dom";
import history from "~/lib/history";
import { clearTooltip, inVideocallMode, inZoomMeeting, isTeams, setTooltip } from "~/lib/utils";
import { zoomOpenUrl } from "~/lib/zoom";
import { environment } from "~/models";
import NameList from "~/ui/avatar/NameList";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type {
	ActionItemLinkageModalItem$data,
	ActionItemLinkageModalItem$key,
} from "~/__generated__/ActionItemLinkageModalItem.graphql";
import type { ActionItemLinkageModalNoteInfo$key } from "~/__generated__/ActionItemLinkageModalNoteInfo.graphql";
import type { ActionItemLinkageModalRendererQuery } from "~/__generated__/ActionItemLinkageModalRendererQuery.graphql";

import styles from "./css/ActionItemLinkageModal.module.css";

const MemoizedFontAwesomeIcon = React.memo(FontAwesomeIcon);

const originalBlockTooltip = setTooltip(
	"Any changes you make here will be visible in all linked series",
	"bottom left",
	undefined,
	true,
	"13.5em",
);

export function ActionItemLink(
	props: ({ blockId: string } | { blockKey: string; noteId: string }) & {
		isOriginal?: boolean;
		onClick?: () => void;
		className?: string;
	},
) {
	const [, forceUpdate] = React.useState();
	const { isMobile } = React.useContext(ViewportContext);
	const { inPanel, view } = React.useContext(ActionItemsContext);
	const inRecording = view === Views.RECORDING;

	React.useEffect(() => {
		const doForceUpdate = () => forceUpdate(undefined);
		window.addEventListener("resize", doForceUpdate);
		return () => window.removeEventListener("resize", doForceUpdate);
	}, [forceUpdate]);

	function openModal(e: React.MouseEvent) {
		if (props.onClick) {
			props.onClick();
		}
		__globalModalContext?.open(({ close }) => <ActionItemLinkageModalRenderer {...props} close={close} />);
		e.preventDefault();
		e.stopPropagation();
	}

	return (
		<div
			className={cx(styles.linkIconContainer, props.className)}
			onClickCapture={openModal}
			{...(props.isOriginal && !isMobile && !inPanel && !inRecording ? originalBlockTooltip : {})}
		>
			<MemoizedFontAwesomeIcon fixedWidth icon={faLink} className={styles.linkItemIcon} />
		</div>
	);
}

const NoteInfo: React.FC<{ block: ActionItemLinkageModalNoteInfo$key; close: () => void }> = ({
	block: blockKey,
	close,
}) => {
	const block = useFragment(
		graphql`
			fragment ActionItemLinkageModalNoteInfo on NoteBlock {
				id
				url
				note {
					id
					title
					noteuserSet {
						id
						user {
							id
							fullName
						}
					}
					stream {
						id
						displayName
						url
						... on NoteStream {
							type
						}
					}
				}
			}
		`,
		blockKey,
	);

	const gotoStream = React.useCallback(() => {
		const { url: blockUrl } = block;
		close();
		if (isTeams || inVideocallMode) {
			const url = new URL(blockUrl, window.location.origin);
			if (inZoomMeeting) {
				zoomOpenUrl(url.toString());
			} else {
				window.open(url.toString());
			}
		} else {
			history.push(blockUrl);
		}
	}, [block, close]);
	return (
		<div className={cx(styles.streamInfo, styles.item)} onClick={gotoStream}>
			<div className={styles.itemTitle}>
				{/* The stream name can be an empty string, it isn't nullable */}
				{block.note.stream.displayName || "Untitled note series"}
			</div>
			<div className={styles.itemMeta}>
				{getTypeIcon(block.note.stream.type || "meeting")}
				<div>
					<NameList useSelfName users={block.note.noteuserSet.map(item => item.user)} maxLength={5} />
				</div>
			</div>
		</div>
	);
};

const ActionItemLinkageModal: React.FC<{ close: () => void; itemKey: ActionItemLinkageModalItem$key }> = ({
	close,
	itemKey,
}) => {
	const item = useFragment(
		graphql`
			fragment ActionItemLinkageModalItem on NoteBlock {
				id
				numNoAccess
				hasAccessToNote
				note {
					id
					stream {
						id
						isTodoStream
					}
				}
				originalBlock {
					id
					hasAccessToNote
					note {
						id
						stream {
							id
							isTodoStream
						}
					}

					childBlocks {
						edges {
							node {
								id
								hasAccessToNote
								note {
									id
									stream {
										id
										isTodoStream
									}
								}
								...ActionItemLinkageModalNoteInfo
							}
						}
					}

					...ActionItemLinkageModalNoteInfo
				}
				childBlocks {
					edges {
						node {
							id
							hasAccessToNote
							note {
								id
								stream {
									id
									isTodoStream
								}
							}
							...ActionItemLinkageModalNoteInfo
						}
					}
				}

				...ActionItemLinkageModalNoteInfo
			}
		`,
		itemKey,
	);
	const relatedBlocks = React.useMemo(() => {
		type NoteType = {
			id: string;
			stream: { isTodoStream: boolean; id: string };
		};
		type ItemType = ActionItemLinkageModalNoteInfo$key & {
			id: string;
			note?: NoteType;
			hasAccessToNote: boolean;
			childBlocks?: ActionItemLinkageModalItem$data["childBlocks"];
		};
		let items: (ItemType | undefined | null)[] = [];
		let relatedBlocks: ItemType[] = [];

		items = items
			.concat([item, item.originalBlock])
			.concat(item.childBlocks.edges.map(e => e?.node))
			.concat(item.originalBlock?.childBlocks.edges.map(e => e?.node) ?? []);

		// group the items be stream in order then find the note in those streams that contains
		// the original action item (if present)
		const itemsByStream: {
			[key: string]: Array<ItemType>;
		} = groupBy(
			compact(items).filter(item => item.note && item.hasAccessToNote && !item.note.stream.isTodoStream),
			item => item.note && item.note.stream.id,
		);

		for (const streamId in itemsByStream) {
			const sortedItems = sortBy(itemsByStream[streamId], item =>
				item.childBlocks !== undefined ? -item.childBlocks.edges.length : 0,
			);
			if (sortedItems[0].note !== undefined) {
				relatedBlocks.push(sortedItems[0]);
			}
		}
		return relatedBlocks;
	}, [item]);
	const count = item.numNoAccess + relatedBlocks.length;
	return (
		<Modal onClose={close}>
			<Modal.Header onClose={close}>
				<Modal.Title>Linked series</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody} noPadding>
				<div className={styles.promptTextContainer}>
					<p>
						This action item was linked in the following {count} stream{count !== 1 ? "s" : ""}:
					</p>
				</div>
				<div className={styles.streams}>
					{relatedBlocks.map(b => {
						return <NoteInfo block={b} key={b.id} close={close} />;
					})}
				</div>
				{!!item.numNoAccess && (
					<div className={cx(styles.noAccess, styles.item)}>
						<div className={styles.itemTitle}>
							{item.numNoAccess} additional {item.numNoAccess === 1 ? "copy" : "copies"}
						</div>
						<div className={styles.itemMeta}>
							<FontAwesomeIcon icon={faLockAlt} fixedWidth />
							You do not have user permissions to access these streams
						</div>
					</div>
				)}
			</Modal.Body>
		</Modal>
	);
};

export const ActionItemLinkageModalRenderer: React.FC<{
	close: () => void;
	blockId?: string;
	blockKey?: string;
	noteId?: string;
}> = ({ close, blockId, blockKey, noteId }) => {
	// need to use this inside prose, where contexts are not available
	const [data, setData] = React.useState<ActionItemLinkageModalRendererQuery["response"]["noteBlock"]>();
	React.useEffect(() => {
		fetchQuery<ActionItemLinkageModalRendererQuery>(
			environment,
			graphql`
				query ActionItemLinkageModalRendererQuery($id: ID, $blockKey: String, $noteId: ID) {
					noteBlock(id: $id, blockKey: $blockKey, noteId: $noteId) {
						...ActionItemLinkageModalItem
					}
				}
			`,
			{ id: blockId, blockKey, noteId },
		)
			.toPromise()
			.then(res => {
				if (res && res.noteBlock) {
					setData(res.noteBlock);
				}
			});
	}, [setData, blockId, blockKey, noteId]);
	if (!data) return null;
	return (
		<RelayEnvironmentProvider environment={environment}>
			<ActionItemLinkageModal itemKey={data} close={close} />
		</RelayEnvironmentProvider>
	);
};

export default ActionItemLinkageModal;

export interface LinkIconData {
	className: string;
	noteId: string;
	blockKey: string;
	isOriginal: boolean;
	onClick: () => void;
	disableClick: boolean;
}

export class LinkIcon implements dom.DOMComponent<LinkIconData> {
	el: HTMLElement;
	private data?: LinkIconData;

	static template = el(
		"div",
		{
			class: styles.linkIconContainer,
			contenteditable: "false",
		},
		dom.render(<FontAwesomeIcon fixedWidth icon={faLink} className={styles.linkItemIcon} />),
	);

	constructor(data: LinkIconData) {
		this.el = dom.cloneNode(LinkIcon.template);
		this.update(data);

		window.addEventListener("resize", this.doUpdate);
		this.el.onclick = this.openModal;
	}

	deinit() {
		window.removeEventListener("resize", this.doUpdate);
	}

	update(data: LinkIconData) {
		if (data.className !== this.data?.className) {
			if (this.data?.className) this.el.classList.remove(this.data.className);
			this.el.classList.add(data.className);
		}

		if (data.disableClick !== this.data?.disableClick) {
			this.el.classList.toggle(styles.clickDisabled, data.disableClick);
		}

		clearTooltip(this.el);
		if (data.isOriginal && !Viewport.isMobile()) {
			setAttr(this.el, originalBlockTooltip);
			if (originalBlockTooltip.style) dom.setStyles(this.el, originalBlockTooltip.style);
		}

		this.data = data;
	}

	private openModal = (e: MouseEvent) => {
		if (!this.data || this.data.disableClick) return;
		const { noteId, blockKey } = this.data;
		this.data.onClick();
		__globalModalContext?.open(({ close }) => (
			<ActionItemLinkageModalRenderer noteId={noteId} blockKey={blockKey} close={close} />
		));
		e.preventDefault();
		e.stopPropagation();
	};

	private doUpdate = () => {
		if (!this.data) return;
		this.update(this.data);
	};
}
